import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SiteConfigClient } from '../../../infrastructure/SiteConfigClient';
import { SiteCacheClient } from '../../../infrastructure/SiteCacheClient';
import { getConfig } from '../../../config';
import { AdminApiDomain, getAdminApiUrl } from '../../../config/ApiRouting';
import type { RedirectRule, AddRedirectRequest, DeleteRedirectRequest } from '../../../infrastructure/SiteConfigClient';

const siteConfigClient = new SiteConfigClient(getAdminApiUrl(AdminApiDomain.SITE_CONFIG));
const siteCacheClient = new SiteCacheClient(getConfig().apiUrl);

export const useRedirect = () => {
  const queryClient = useQueryClient();

  const redirectsQuery = useQuery({
    queryKey: ['redirects'],
    queryFn: () => siteConfigClient.getRedirectMappings(),
    staleTime: 2 * 60 * 1000,
  });

  const addRedirectMutation = useMutation({
    mutationFn: (request: AddRedirectRequest) => siteConfigClient.addRedirect(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['redirects'] });
    },
  });

  const deleteRedirectMutation = useMutation({
    mutationFn: (request: DeleteRedirectRequest) => siteConfigClient.deleteRedirect(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['redirects'] });
    },
  });

  const purgeCacheMutation = useMutation({
    mutationFn: () => siteConfigClient.purgeCache(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-cache', 'is-invalidating'] });
    },
  });

  const isInvalidatingQuery = useQuery({
    queryKey: ['site-cache', 'is-invalidating'],
    queryFn: () => siteCacheClient.isInvalidating(),
    refetchInterval: (query) => {
      return query.state.data?.isInvalidating ? 2000 : false;
    },
  });

  return {
    redirectsQuery,
    addRedirectMutation,
    deleteRedirectMutation,
    purgeCacheMutation,
    isInvalidating: isInvalidatingQuery.data?.isInvalidating ?? false,
    isLoading: isInvalidatingQuery.isLoading,
  };
};

export type { RedirectRule, AddRedirectRequest, DeleteRedirectRequest };
